/**
 * Follow-ups — the numbers on a saved-list page (redesign Phase 3, 2026-10-02).
 *
 * Plan: docs/plans/2026-10-02-followups-redesign-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). The two rules it needs from
 * its siblings are INJECTED, as rowModel.js takes them:
 *   labelOf     row → the label the table's Status column shows (rowModel statusLabelOf)
 *   categoryOf  (label, { teamioFollowupStatusID }) → colour category (statusCategory)
 *
 * ── WHAT IT REPLACES ───────────────────────────────────────────────────────────────────────────
 * The old list pages (OverdueRecallList.vue / UnscheduledList.vue) draw two donut charts:
 * getAttemptsData — one slice per distinct Attempts value — and getOutcomeData — one slice per
 * distinct Status. The redesign draws three cards from the same rows:
 *   Worked so far        patients with at least one attempt, of everyone on the list
 *   Follow-up attempts   patients by attempt count, bucketed 0 / 1 / 2 / 3 or more
 *   Outcome              patients by status label, largest first, coloured by category
 *
 * ── HOW ATTEMPTS ARE COUNTED ───────────────────────────────────────────────────────────────────
 * From the row's Attempts counter, exactly as the table's Attempts column reads it (rowModel
 * attemptsOf): null, blank or not a number is 0 — legacy getAttemptsData also turned null into 0.
 * AttemptDetails only says WHICH kinds (call / text / email); it never changes the count, so a
 * row whose counter and details disagree reads the same here as in its Attempts cell.
 *
 * ── THE FILTERS A LIST WAS MADE WITH ───────────────────────────────────────────────────────────
 * createList stores the main page's filters on the list row as display strings (FilterDates,
 * FilterProvider, FilterCarriers, FilterCodes, FilterAmountRemainingOver — OverdueRecall.vue /
 * UnscheduledTreatment.vue saveList). The old list pages' "Filters:" block never shows them
 * (`filtersApplied` is never set true). listFilters() shows the ones that carry a value. The save
 * builds FilterProvider as `${selectedProvider.FName} ${selectedProvider.LName}` on an ARRAY, so a
 * real list often holds "undefined undefined" — a value made only of undefined / null words is
 * treated as blank, never printed.
 */

/** The four attempt buckets, in bar order. `min` is the lowest count in the bucket. */
export const ATTEMPT_BUCKETS = [
  { key: 'a0', label: 'No attempts yet', min: 0 },
  { key: 'a1', label: '1 attempt', min: 1 },
  { key: 'a2', label: '2 attempts', min: 2 },
  { key: 'a3', label: '3 or more', min: 3 },
];

/** What the Outcome card calls a row whose status label is blank. */
export const NO_STATUS_LABEL = 'No status';

/** The row's attempt count: Attempts as a whole number ≥ 0; null / blank / unreadable → 0. */
export function attemptCount(row) {
  const v = row ? row.Attempts : null;
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function pctOf(count, total) {
  return total > 0 ? (count / total) * 100 : 0;
}

function defaultLabelOf(row) {
  return row.Status === null || row.Status === undefined ? '' : String(row.Status);
}

/**
 * The three cards' numbers for a list's rows.
 * @param {Array}  rows
 * @param {Object} [opts]  { labelOf, categoryOf } — see the header
 * @returns {{ total, worked, workedPct, attempts: Array, outcomes: Array }}
 *   attempts  all four buckets in order, each { key, label, count, pct } (count may be 0)
 *   outcomes  one per label, each { label, category, count, pct }, largest first, then A→Z.
 *             A label's category is the one of its FIRST row (rows normally agree; a
 *             "Do Not Follow-up" TeamioFollowupStatusID on one row is the rare exception).
 */
export function summarizeList(rows, opts) {
  const o = opts || {};
  const labelOf = typeof o.labelOf === 'function' ? o.labelOf : defaultLabelOf;
  const categoryOf = typeof o.categoryOf === 'function' ? o.categoryOf : () => 'none';
  const list = (Array.isArray(rows) ? rows : []).filter(r => r && typeof r === 'object');
  const total = list.length;

  const tally = ATTEMPT_BUCKETS.map(() => 0);
  let worked = 0;
  const byLabel = {};
  const order = [];

  list.forEach((r) => {
    const n = attemptCount(r);
    if (n > 0) worked += 1;
    tally[Math.min(ATTEMPT_BUCKETS.length - 1, n)] += 1;

    const raw = labelOf(r);
    const label = raw === null || raw === undefined || String(raw).trim() === '' ? NO_STATUS_LABEL : String(raw).trim();
    if (!byLabel[label]) {
      const category = label === NO_STATUS_LABEL
        ? 'none'
        : (categoryOf(label, { teamioFollowupStatusID: r.TeamioFollowupStatusID }) || 'none');
      byLabel[label] = { label, category, count: 0 };
      order.push(label);
    }
    byLabel[label].count += 1;
  });

  const outcomes = order
    .map(l => byLabel[l])
    .sort((a, b) => b.count - a.count || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0))
    .map(x => Object.assign({}, x, { pct: pctOf(x.count, total) }));

  return {
    total,
    worked,
    workedPct: pctOf(worked, total),
    attempts: ATTEMPT_BUCKETS.map((b, i) => ({ key: b.key, label: b.label, count: tally[i], pct: pctOf(tally[i], total) })),
    outcomes,
  };
}

/** A stored filter string, or '' when it is blank or made only of "undefined" / "null" words. */
export function cleanFilterValue(value) {
  if (value === null || value === undefined) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (s.split(/\s+/).every(w => w === 'undefined' || w === 'null')) return '';
  return s;
}

/**
 * The filters a saved list was made with, as [{ key, label, value }] — only the ones with a value.
 * @param {Object} list   the teamio.lists row (getList)
 * @param {Object} [opts] { datesLabel, currency } — the date filter's name on this queue (the old
 *                        pages print "Tx Plan Date" on both, which is wrong for recall) and the
 *                        symbol before the amount (the old pages hard-code '$')
 */
export function listFilters(list, opts) {
  const l = list || {};
  const o = opts || {};
  const currency = o.currency || '$';
  const out = [];
  const add = (key, label, value) => { if (value) out.push({ key, label, value }); };

  add('dates', o.datesLabel || 'Dates', cleanFilterValue(l.FilterDates));
  add('providers', 'Providers', cleanFilterValue(l.FilterProvider));
  // Saved as "Delta,Aetna" — give the commas a space so a long list can wrap.
  add('carriers', 'Carriers', cleanFilterValue(l.FilterCarriers).split(',').map(s => s.trim()).filter(Boolean).join(', '));
  add('codes', 'Include codes', cleanFilterValue(l.FilterCodes).split(',').map(s => s.trim()).filter(Boolean).join(', '));
  const amount = cleanFilterValue(l.FilterAmountRemainingOver);
  if (amount && Number.isFinite(Number(amount))) {
    if (Number(amount) > 0) add('amount', 'Amount remaining over', `${currency}${amount}`);
  } else {
    add('amount', 'Amount remaining over', amount);
  }
  return out;
}
